import { useMemo } from "react";

/**
 * Extract commodity name from symbol
 * @param {string} symbol - e.g., "MCX:CRUDEOIL23OCT..."
 * @returns {string|null} - e.g., "CRUDEOIL" or null
 */
function getCommodityFromSymbol(symbol) {
    try {
        const s = symbol ? symbol.toUpperCase() : "";
        
        // Exclude options (CE/PE)
        if (s.endsWith("CE") || s.endsWith("PE")) return null;

        const isFUT = s.endsWith("FUT");
        if (!s.includes("MCX") && !s.includes("NFO") && !s.includes("BFO") && !isFUT) return null;

        // Remove prefixes
        const cleanSymbol = s.replace("MCX:", "").replace("NFO:", "").replace("BFO:", "").replace("NSE:", "").replace("BSE:", "");

        // Common Commodities List (add more if needed)
        const commodities = [
            "CRUDEOIL", "GOLD", "SILVER", "NATURALGAS",
            "COPPER", "ZINC", "LEAD", "ALUMINIUM", "MENTHAOIL", "COTTON"
        ];

        // Check against known list first
        for (const comm of commodities) {
            if (cleanSymbol.startsWith(comm)) return comm;
        }

        // Fallback: Regex to capture name before digits
        // e.g. GOLDM24FEB... -> GOLDM ? No, usually CRUDEOIL is consistent
        // Let's rely on the list or a generic split
        const match = cleanSymbol.match(/^([A-Z]+)/);
        return match ? match[1] : null;

    } catch (e) {
        console.error("Error extracting commodity from symbol:", symbol, e);
        return null;
    }
}

/**
 * Normalize order/position side to 'buy' | 'sell' | null
 */
function normalizeSide(rawSide) {
    if (rawSide === null || rawSide === undefined) return null;
    const s = String(rawSide).trim().toLowerCase();
    if (s === "1" || s === "buy" || s === "b") return "buy";
    if (s === "2" || s === "-1" || s === "sell" || s === "s") return "sell";
    return null;
}

/**
 * Helper: Get multiplier from userMappings for a given client and commodity
 * For commodities, we might not have specific columns per commodity in the table yet.
 * If not, we might need to assume 1 or look for a generic config.
 * For now, returning 1 as default if not found.
 */
function getMultiplier(userMappings, clientId, commodity) {
    // If you have specific columns for commodities in Postgres, add logic here.
    // e.g. crudeoil_multiplier
    if (!userMappings || !clientId || !commodity) return 1;
    const mapping = userMappings.find((u) => u.client_id === clientId);
    if (!mapping) return 1;

    // Try to find a specific key, otherwise 1
    const key = `${commodity.toLowerCase()}_multiplier`;
    const val = Number(mapping[key]);
    return val > 0 ? val : 1;
}

/**
 * Mapping from commodity name → the corresponding pledgeplus FUT multiplier field
 * from public.lots_config_pledgeplus_fut
 */
const PP_FUT_MULTIPLIER_MAP = {
    GOLD:       'pledgeplus_mcx_multiplier',
    GOLDM:      'pledgeplus_mcx_multiplier',
    SILVER:     'pledgeplus_mcx_multiplier',
    SILVERM:    'pledgeplus_mcx_multiplier',
    CRUDEOIL:   'pledgeplus_mcx_multiplier',
    NATURALGAS: 'pledgeplus_mcx_multiplier',
    COPPER:     'pledgeplus_mcx_multiplier',
    ZINC:       'pledgeplus_mcx_multiplier',
    LEAD:       'pledgeplus_mcx_multiplier',
    ALUMINIUM:  'pledgeplus_mcx_multiplier',
    MENTHAOIL:  'pledgeplus_mcx_multiplier',
    COTTON:     'pledgeplus_mcx_multiplier',
    // Index futures
    NIFTY:      'pledgeplus_nifty_multiplier',
    BANKNIFTY:  'pledgeplus_banknifty_multiplier',
    FINNIFTY:   'pledgeplus_finnifty_multiplier',
    MIDCPNIFTY: 'pledgeplus_midcpnifty_multiplier',
    SENSEX:     'pledgeplus_sensex_multiplier',
};

const PP_MINI_MCX_FIELD = 'pledgemini_mcx_multiplier';

/**
 * Get both the existing multiplier AND the new PledgePlus FUT multipliers.
 * Returns { mult, futMult, miniMult }
 *   mult     — existing commodity multiplier (e.g. crudeoil_multiplier), default 1
 *   futMult  — pledgeplus FUT multiplier from lots_config_pledgeplus_fut, null if not set
 *   miniMult — pledgemini_mcx_multiplier from lots_config_pledgeplus_fut, null if not set
 */
function getMultipliers(userMappings, clientId, commodity) {
    if (!userMappings || !clientId || !commodity) return { mult: 1, futMult: null, miniMult: null };
    const mapping = userMappings.find((u) => u.client_id === clientId);
    if (!mapping) return { mult: 1, futMult: null, miniMult: null };

    const key = `${commodity.toLowerCase()}_multiplier`;
    const val = Number(mapping[key]);
    const mult = val > 0 ? val : 1;

    const futFieldName = PP_FUT_MULTIPLIER_MAP[commodity.toUpperCase()];
    const futRaw = futFieldName ? Number(mapping[futFieldName]) : NaN;
    const futMult = (!isNaN(futRaw) && futRaw > 0) ? futRaw : null;

    const miniRaw = Number(mapping[PP_MINI_MCX_FIELD]);
    const miniMult = (!isNaN(miniRaw) && miniRaw > 0) ? miniRaw : null;

    return { mult, futMult, miniMult };
}



/**
 * Calculate MODE (most frequent value) from an array of numbers
 */
function calculateMode(numbers) {
    if (!numbers || numbers.length === 0) return 0;
    const frequency = {};
    let maxFreq = 0, mode = numbers[0];
    numbers.forEach(num => {
        frequency[num] = (frequency[num] || 0) + 1;
        if (frequency[num] > maxFreq) { maxFreq = frequency[num]; mode = num; }
    });
    return mode;
}

/**
 * Calculate base quantity for each unique symbol+strategy combination
 */
function calculateSymbolStrategyBaseQty(positions, userMappings) {
    const symbolStrategyQtyMap = {};

    positions.forEach((position) => {
        const commodity = getCommodityFromSymbol(position.symbol);
        if (!commodity) return;

        const netQty = position.net_qty || position.qty || 0;
        if (Math.abs(netQty) === 0) return;

        // Filter out N/A strategies and 'zerofolio'
        if (!position.strategy || position.strategy === "N/A" || position.strategy === "N" || position.strategy === "ZEROFOLIO") return;



        const key = `${position.symbol}_${position.strategy || "NO_STRATEGY"}`;

        if (!symbolStrategyQtyMap[key]) {
            symbolStrategyQtyMap[key] = {
                symbol: position.symbol,
                strategy: position.strategy,
                index: commodity, // Using 'index' property name for compatibility with Modal
                normalizedQuantities: [],
            };
        }


        const clientId = position.client_id;
        const qty = Math.abs(netQty);
        const multiplier = getMultiplier(userMappings, clientId, commodity);
        const normalizedQty = qty / multiplier; // If mult is 1, it's just qty

        symbolStrategyQtyMap[key].normalizedQuantities.push(normalizedQty);
    });



    const baseQtyMap = {};
    Object.entries(symbolStrategyQtyMap).forEach(([key, data]) => {
        const mode = calculateMode(data.normalizedQuantities);
        baseQtyMap[key] = {
            symbol: data.symbol,
            strategy: data.strategy,
            index: data.index,
            baseQty: mode,
            clients: data.normalizedQuantities.length,
        };
    });

    return baseQtyMap;
}

/**
 * Group positions by client and commodity (not by side)
 * We want to aggregate all positions for a client+commodity together
 */
function groupPositionsByClientCommodity(positions) {
    const grouped = {};

    positions.forEach((position) => {
        const commodity = getCommodityFromSymbol(position.symbol);
        if (!commodity) return;

        const netQty = position.net_qty || position.qty || 0;
        if (Math.abs(netQty) === 0) return;

        // Filter out N/A strategies
        if (!position.strategy || position.strategy === "N/A" || position.strategy === "N" || position.strategy === "ZEROFOLIO") return;

        const key = `${position.client_id}_${commodity}`;

        if (!grouped[key]) {
            grouped[key] = {
                client_id: position.client_id,
                commodity,
                qty: 0,
                netSignedQty: 0, // Track signed qty
                longQty: 0,
                shortQty: 0,
                pnl: 0,
                positions: [],
            };
        }

        // Use absolute value of net quantity
        const qty = Math.abs(netQty);
        const signedQty = netQty; // Track signed qty
        const pnl = position.pl || position.pnl || 0;

        grouped[key].qty += qty;
        grouped[key].netSignedQty += signedQty; // Accumulate signed qty

        if (signedQty > 0) grouped[key].longQty += signedQty;
        if (signedQty < 0) grouped[key].shortQty += Math.abs(signedQty);

        grouped[key].pnl += pnl;
        grouped[key].positions.push(position);
    });

    return grouped;
}

/**
/**
 * Group and store sell orders by client, commodity, and side
 * Only includes orders with status="Pending" AND status_no=6
 * NOW stores the ACTUAL ORDERS array, not just qty
 */
/**
 * Group and store orders by client, commodity, and side
 * Only includes orders with status="Pending" AND status_no=6
 * NOW stores the ACTUAL ORDERS array, not just qty
 */
function groupOrdersByClientCommoditySide(orders) {
    const grouped = {};

    orders.forEach((order) => {
        // Robust Key Handling
        const clientId = order.clientId || order.client_id;
        if (!clientId) return;

        const commodity = getCommodityFromSymbol(order.symbol);
        if (!commodity) return;

        const sideStr = String(order.side || "").toLowerCase();

        // Allow both BUY and SELL
        const isSell = sideStr === "sell" || sideStr === "-1" || sideStr === "2";
        const isBuy = sideStr === "buy" || sideStr === "1";
        const normalized = isSell ? "sell" : (isBuy ? "buy" : null);

        if (!normalized) return;

        // Strict Status Check matching Modal logic (Pending AND 6)
        const statusText = (order.status || "").toLowerCase();
        const statusNo = order.status_no !== undefined ? parseInt(order.status_no, 10) : undefined;

        // STRICT CHECK: must be pending AND status_no 6
        const isPending = statusText === "pending" && statusNo === 6;

        if (!isPending) return;

        const key = `${clientId}_${commodity}_${normalized}`;

        if (!grouped[key]) {
            grouped[key] = {
                client_id: clientId,
                commodity,
                side: normalized,
                orders: [], // Store orders
            };
        }

        // Add Order
        grouped[key].orders.push(order);
    });

    return grouped;
}

/**
 * Main Hook
 */
export function useCommoditiesView(
    positions,
    orders,
    userMappings,
    positionsUpdateVersion
) {
    const commoditiesViewData = useMemo(() => {
        if (!positions || positions.length === 0) {
            return { data: [], baseQtyMap: {}, activeCommodities: [] };
        }

        const groupedPositions = groupPositionsByClientCommodity(positions);
        const symbolStrategyBaseQty = calculateSymbolStrategyBaseQty(positions, userMappings);

        // Group sell orders (now containing array of orders)
        const groupedOrders = orders ? groupOrdersByClientCommoditySide(orders) : {};

        // ------------------------------------------------------
        // 1. Build Detailed Position Map (Client_Symbol_Strategy)
        // ------------------------------------------------------
        const detailedPositionMap = {};
        if (positions && positions.length > 0) {
            positions.forEach(pos => {
                const clientId = pos.clientId || pos.client_id;
                if (!clientId) return;

                const strat = (pos.strategy || "").toString().trim().toUpperCase() || "NO_STRATEGY";
                // Remove prefixes like MCX:
                const cleanSym = (pos.symbol || "").toString().trim().toUpperCase().replace(/^(MCX|NSE|NFO|BFO|CDS):/i, "");

                const key = `${clientId}_${cleanSym}_${strat}`;

                if (!detailedPositionMap[key]) {
                    detailedPositionMap[key] = {
                        netQty: 0,
                        symbol: pos.symbol,
                        strategy: strat
                    };
                }
                detailedPositionMap[key].netQty += (pos.net_qty || pos.qty || 0);
            });
        }

        // ------------------------------------------------------
        // 2. Calculate SL Metrics (Iterate Orders & Check against Map)
        // ------------------------------------------------------
        const slMetricsMap = {}; // { clientId_commodity: { slBuyQty: 0, slSellQty: 0 } }

        if (orders) {
            orders.forEach(order => {
                const clientId = order.clientId || order.client_id;
                if (!clientId) return;

                const commodity = getCommodityFromSymbol(order.symbol);
                if (!commodity) return;

                // VALID STATUS CHECK (Pending / 6)
                const statusText = (order.status || "").toLowerCase();
                const statusNo = order.status_no !== undefined ? parseInt(order.status_no, 10) : undefined;
                const isPending = statusText === "pending" && statusNo === 6;
                if (!isPending) return;

                // Determine Side
                const sideStr = String(order.side || "").toLowerCase();
                const isBuy = sideStr === "buy" || sideStr === "1";
                const isSell = sideStr === "sell" || sideStr === "-1" || sideStr === "2";
                if (!isBuy && !isSell) return;

                // CHECK IF SL (Matches Position + Opposite Side)
                const strat = (order.strategy || "").toString().trim().toUpperCase() || "NO_STRATEGY";
                const cleanSym = (order.symbol || "").toString().trim().toUpperCase().replace(/^(MCX|NSE|NFO|BFO|CDS):/i, "");
                const posKey = `${clientId}_${cleanSym}_${strat}`;

                const position = detailedPositionMap[posKey];
                let isSL = false;

                if (position) {
                    const posNetQty = position.netQty;
                    const isLong = posNetQty > 0;
                    const isShort = posNetQty < 0;

                    // SL Logic: Position Long + Order Sell OR Position Short + Order Buy
                    if ((isLong && isSell) || (isShort && isBuy)) {
                        isSL = true;
                    }
                } else {
                    // No Position Match -> Orphan / New Entry -> NOT AN SL
                    isSL = false;
                }

                if (isSL) {
                    const key = `${clientId}_${commodity}`;
                    if (!slMetricsMap[key]) slMetricsMap[key] = { slBuyQty: 0, slSellQty: 0 };

                    const qty = Math.abs(parseFloat(order.qty || 0));
                    if (isBuy) slMetricsMap[key].slBuyQty += qty;
                    if (isSell) slMetricsMap[key].slSellQty += qty;
                }
            });
        }

        // Track matched order IDs to find orphans later
        const matchedOrderIds = new Set();
        const clientCommodityMap = {};

        Object.values(groupedPositions).forEach((posGroup) => {
            const { client_id, commodity, qty, netSignedQty, longQty, shortQty, pnl, positions: positionsInGroup } = posGroup;
            if (!clientCommodityMap[client_id]) clientCommodityMap[client_id] = {};

            // Get SL Data from Pre-calculated Map
            const slData = slMetricsMap[`${client_id}_${commodity}`] || { slBuyQty: 0, slSellQty: 0 };

            if (!clientCommodityMap[client_id][commodity]) {
                clientCommodityMap[client_id][commodity] = {
                    qty: 0, slQty: 0, pnl: 0, netSignedQty: 0,
                    longQty: 0, shortQty: 0,
                    slBuyQty: 0, slSellQty: 0,
                    // New Fields for Covered/Reversal Logic
                    slSellCovered: 0, slSellReversal: 0,
                    slBuyCovered: 0, slBuyReversal: 0,
                    positions: []
                };
            }
            clientCommodityMap[client_id][commodity].qty += qty;
            clientCommodityMap[client_id][commodity].pnl += pnl;
            clientCommodityMap[client_id][commodity].netSignedQty += netSignedQty;
            clientCommodityMap[client_id][commodity].longQty += longQty;
            clientCommodityMap[client_id][commodity].shortQty += shortQty;
            clientCommodityMap[client_id][commodity].positions = (clientCommodityMap[client_id][commodity].positions || []).concat(positionsInGroup);

            // Apply granular SL quantities
            clientCommodityMap[client_id][commodity].slBuyQty += slData.slBuyQty;
            clientCommodityMap[client_id][commodity].slSellQty += slData.slSellQty;

            // Calculate Covered vs Reversal
            // Long Position matches with Sell Orders
            const longPos = clientCommodityMap[client_id][commodity].longQty;
            const sellOrdersTotal = clientCommodityMap[client_id][commodity].slSellQty;
            clientCommodityMap[client_id][commodity].slSellCovered = Math.min(longPos, sellOrdersTotal);
            clientCommodityMap[client_id][commodity].slSellReversal = Math.max(0, sellOrdersTotal - longPos);

            // Short Position matches with Buy Orders
            const shortPos = clientCommodityMap[client_id][commodity].shortQty;
            const buyOrdersTotal = clientCommodityMap[client_id][commodity].slBuyQty;
            clientCommodityMap[client_id][commodity].slBuyCovered = Math.min(shortPos, buyOrdersTotal);
            clientCommodityMap[client_id][commodity].slBuyReversal = Math.max(0, buyOrdersTotal - shortPos);


            // Calculate SL Qty based on Position Side
            // If Long (netSignedQty > 0), SLs are SELLS.
            // If Short (netSignedQty < 0), SLs are BUYS.
            // If Flat (0), SL Qty is 0 (or sum of both? Usually 0 as no position to protect).
            if (clientCommodityMap[client_id][commodity].netSignedQty > 0) {
                clientCommodityMap[client_id][commodity].slQty = clientCommodityMap[client_id][commodity].slSellQty;
            } else if (clientCommodityMap[client_id][commodity].netSignedQty < 0) {
                clientCommodityMap[client_id][commodity].slQty = clientCommodityMap[client_id][commodity].slBuyQty;
            } else {
                clientCommodityMap[client_id][commodity].slQty = 0;
            }

            // Determine expected match side based on netSignedQty
            // netSignedQty > 0 => Long Position => Expect SELL orders
            // netSignedQty < 0 => Short Position => Expect BUY orders
            const expectedOrderSide = netSignedQty > 0 ? "sell" : (netSignedQty < 0 ? "buy" : null);

            // Track which orders match this position's symbols and strategies
            positionsInGroup.forEach(pos => {
                if (orders) {
                    orders.forEach(o => {
                        const oClientId = o.clientId || o.client_id;
                        const sideStr = String(o.side || "").toLowerCase();

                        // Check side matches expectation
                        const isSell = sideStr === "sell" || sideStr === "-1" || sideStr === "2";
                        const isBuy = sideStr === "buy" || sideStr === "1";
                        const orderSide = isSell ? "sell" : (isBuy ? "buy" : null);

                        if (orderSide !== expectedOrderSide) return;

                        const statusText = (o.status || "").toLowerCase();
                        const statusNo = o.status_no !== undefined ? parseInt(o.status_no, 10) : undefined;
                        const isPending = statusText === "pending" && statusNo === 6;
                        if (!isPending) return;

                        // Check if order matches this position
                        const cleanPosSymbol = (pos.symbol || "").replace(/^MCX:/i, "").trim();
                        const cleanOrderSymbol = (o.symbol || "").replace(/^MCX:/i, "").trim();
                        const posStrategy = (pos.strategy || "").toString().trim().toUpperCase();
                        const orderStrategy = (o.strategy || "").toString().trim().toUpperCase();

                        if (cleanPosSymbol === cleanOrderSymbol && posStrategy === orderStrategy && oClientId === client_id) {
                            matchedOrderIds.add(o.order_id || o.orderid);
                        }
                    });
                }
            });

            // Match Orders (SL Qty) strictly by Client ID and Expected Side
            // Legacy/Modal logic still relies on matching specific orders to specific rows
            // But for the overview table, we use the aggregated `slBuyQty` / `slSellQty` calculated above.

            // let totalSlQty = 0;
            // const k = `${client_id}_${commodity}_${expectedOrderSide}`;
            // ... (Removed legacy calculation block to avoid overwriting)

        });

        // console.log("📋 Matched Order IDs (orders with positions):", Array.from(matchedOrderIds));

        // Add orphaned orders (orders without matching positions)
        // console.log("🔍 Checking for orphaned orders. Total orders:", orders?.length);
        if (orders) {
            let orphanCount = 0;
            orders.forEach(o => {
                const oClientId = o.clientId || o.client_id;
                if (!oClientId) return;

                const commodity = getCommodityFromSymbol(o.symbol);
                if (!commodity) return;

                const sideStr = String(o.side || "").toLowerCase();
                const isSell = sideStr === "sell" || sideStr === "-1" || sideStr === "2";
                const isBuy = sideStr === "buy" || sideStr === "1";

                // We care about orphans matching potential SLs (Sell or Buy)
                // But traditionally "orphans" in this context meant "Sell orders without positions".
                // Now it means "Any pending order that wasn't matched to a position".
                if (!isSell && !isBuy) return;

                const statusText = (o.status || "").toLowerCase();
                const statusNo = o.status_no !== undefined ? parseInt(o.status_no, 10) : undefined;
                const isPending = statusText === "pending" && statusNo === 6;
                if (!isPending) return;

                const orderId = o.order_id || o.orderid;
                // If this order was already matched to a position, skip it
                if (matchedOrderIds.has(orderId)) {
                    // console.log("⏭️ Order already matched:", orderId, "Client:", oClientId, "Symbol:", o.symbol);
                    return;
                }

                // // This is an orphaned order - add it to the map
                // console.log("🚨 ORPHANED ORDER FOUND:", {
                //     orderId,
                //     clientId: oClientId,
                //     symbol: o.symbol,
                //     commodity,
                //     qty: o.qty,
                //     side: o.side,
                //     status: o.status,
                //     status_no: o.status_no
                // });
                orphanCount++;

                // Use the actual client ID from the order
                let entryExists = clientCommodityMap[oClientId] && clientCommodityMap[oClientId][commodity];

                // If entry doesn't exist, it means we have no position.
                // Since this order is an Orphan (Entry), and we don't count it as SL,
                // we should NOT create a map entry, to avoid showing a "0/0" column in the table.
                if (!entryExists) {
                    return;
                }

                if (!clientCommodityMap[oClientId]) clientCommodityMap[oClientId] = {};
                if (!clientCommodityMap[oClientId][commodity]) {
                    clientCommodityMap[oClientId][commodity] = { qty: 0, slQty: 0, pnl: 0, netSignedQty: 0 };
                }

                // Only add if entry didn't exist (i.e. no positions)
                // If it existed, the main loop already summed ALL sell orders (including this orphan).
                if (!entryExists) {
                    // clientCommodityMap[oClientId][commodity].slQty += Math.abs(o.qty || 0); // REMOVED: Orphans are Entries, not SLs
                }
            });
            console.log("✅ Total orphaned orders found:", orphanCount);
        }

        // Build Data
        const data = [];
        Object.keys(clientCommodityMap).forEach((clientId) => {
            let userMapping = null;
            if (userMappings) {
                const matches = userMappings.filter((u) => u.client_id === clientId);
                userMapping = matches.find(u => (u.broker || "").toLowerCase() === "zerodha") || matches[0];
            }

            const rowData = {
                clientId,
                clientName: userMapping ? userMapping.name : `Client ${clientId}`,
                status: userMapping ? userMapping.status : "unknown",
                broker: userMapping ? userMapping.broker : "unknown",
            };

            const commodities = Object.keys(clientCommodityMap[clientId]);

            commodities.forEach(comm => {
                const commData = clientCommodityMap[clientId][comm];
                const { mult, futMult, miniMult } = getMultipliers(userMappings, clientId, comm);

                // Color Logic: Red if Qty != SL Qty, else Green
                const color = commData.qty !== commData.slQty ? "red" : "green";

                rowData[comm] = {
                    multiplier: mult,
                    futMult,    // pledgeplus FUT multiplier (null if not configured)
                    miniMult,   // pledgemini MCX multiplier (null if not configured)
                    qty: commData.qty,
                    slQty: commData.slQty,
                    pnl: commData.pnl,
                    netSignedQty: commData.netSignedQty,
                    longQty: commData.longQty,
                    shortQty: commData.shortQty,
                    slBuyQty: commData.slBuyQty,
                    slSellQty: commData.slSellQty,
                    slSellCovered: commData.slSellCovered,
                    slSellReversal: commData.slSellReversal,
                    slBuyCovered: commData.slBuyCovered,
                    slBuyReversal: commData.slBuyReversal,
                    positions: commData.positions || [],
                    color
                };
            });

            data.push(rowData);
        });

        const activeCommodities = Array.from(new Set(
            data.flatMap(row => Object.keys(row).filter(k =>
                k !== 'clientId' && k !== 'clientName' && k !== 'status' && k !== 'broker'
            ))
        )).sort();

        // Base Qty Map (aggregated by commodity)
        const baseQtyMap = {};

        activeCommodities.forEach(comm => {
            const allNormalizedQtys = [];
            data.forEach(row => {
                const cell = row[comm];
                if (cell && cell.qty > 0) {
                    const m = cell.multiplier > 0 ? cell.multiplier : 1;
                    allNormalizedQtys.push(cell.qty / m);
                }
            });

            if (allNormalizedQtys.length > 0) {
                baseQtyMap[comm] = calculateMode(allNormalizedQtys);
            }
        });


        return { data, baseQtyMap, activeCommodities, symbolStrategyBaseQty };

    }, [positions, orders, userMappings]);

    return commoditiesViewData;
}
